import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faEllipsisH, faEyeSlash, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { confirmAlert } from 'react-confirm-alert';
import ConfirmPrompt from '../General/ConfirmPrompt';
import ShopmyImage from '../General/ShopmyImage';
import Tooltip from '../General/Tooltip';
import { formatEasternDate, formatAmountInCurrency } from '../../helpers/formatting';
import './SalesAndPromotionsPanelResult.scss';

const STATUS_LABELS = { live: 'Live now', upcoming: 'Upcoming', ended: 'Expired', unavailable: 'Unavailable' };

const SalesAndPromotionsPanelResult = ({ sale, saving, updateSale }) => {
  const [expanded, setExpanded] = useState(false);
  const selectedPreviewAudience = sale.creatorPreviewAudience?.type === 'lists';
  const previewAudienceDescription =
    'Only creators on the brand’s selected lists can see the early preview. When live, the sale is available to all creators unless hidden.';
  const confirmHide = () => {
    if (sale.isHidden) return updateSale(sale, { isHidden: false });
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Hide this sale from all creators?'
          hideInput
          subheader='It will be removed for every creator, including saved results and creators on the brand’s selected lists. The brand’s sale will continue. Restoring it here will respect the brand’s preview audience and dates.'
          submitBtnDisplay='Hide sale'
          onCancel={onClose}
          onSubmit={() => {
            onClose();
            updateSale(sale, { isHidden: true });
          }}
        />
      )
    });
  };
  return (
    <tbody className='sales-and-promotions-panel-result'>
      <tr>
        <td className='sale-name'>
          <div className='sale-summary'>
            <div className='sale-thumbnail'>
              <ShopmyImage source={sale.cardImage || sale.defaultImage} alt='' loading='lazy' />
            </div>
            <div className='sale-copy'>
              <span className='brand-name'>{sale.brandName}</span>
              <button type='button' onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-controls={`sale-details-${sale.id}`}>
                {sale.title}
              </button>
              <span className='sale-domain'>{sale.domain}</span>
            </div>
          </div>
        </td>
        <td>
          <span className={cn('sale-status', sale.displayState)}>{STATUS_LABELS[sale.displayState]}</span>
          {selectedPreviewAudience && sale.displayState === 'upcoming' && !sale.isHidden && (
            <Tooltip message={previewAudienceDescription} outerClassName='preview-audience-tooltip'>
              <button
                type='button'
                className='preview-audience'
                aria-label={`Selected creators: preview audience for ${sale.title}`}
                aria-expanded={expanded}
                aria-controls={`sale-details-${sale.id}`}
                onClick={() => setExpanded(!expanded)}
              >
                Selected creators <FontAwesomeIcon icon={faInfoCircle} />
              </button>
            </Tooltip>
          )}
          {sale.isHidden && (
            <span className='hidden-label'>
              <FontAwesomeIcon icon={faEyeSlash} /> Hidden from all creators
            </span>
          )}
        </td>
        <td className='sale-dates'>
          <span>{formatEasternDate(sale.claimStartsAt) || 'No start date'}</span>
          <span>to {formatEasternDate(sale.claimEndsAt) || 'No end date'}</span>
        </td>
        <td className='added-date'>{formatEasternDate(sale.createdAt) || '—'}</td>
        <td>
          <button
            type='button'
            className={cn('feature-sale', { active: sale.isFeatured })}
            disabled={saving}
            aria-pressed={sale.isFeatured}
            aria-label={`${sale.isFeatured ? 'Unfeature' : 'Feature'} ${sale.brandName}: ${sale.title}`}
            onClick={() => updateSale(sale, { isFeatured: !sale.isFeatured })}
          >
            <FontAwesomeIcon icon={faStar} /> {sale.isFeatured ? 'Featured' : 'Mark as featured'}
          </button>
        </td>
        <td>
          <button
            type='button'
            className='more-actions'
            aria-label={`Details and actions for ${sale.title}`}
            aria-expanded={expanded}
            aria-controls={`sale-details-${sale.id}`}
            onClick={() => setExpanded(!expanded)}
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr id={`sale-details-${sale.id}`}>
          <td colSpan={6} className='sale-details'>
            <div className='detail-heading'>
              <h3>{sale.title}</h3>
              <button type='button' className='hide-sale' disabled={saving} onClick={confirmHide}>
                <FontAwesomeIcon icon={faEyeSlash} /> {sale.isHidden ? 'Show to eligible creators' : 'Hide from all creators'}
              </button>
            </div>
            {sale.description && <p>{sale.description}</p>}
            {sale.internalName && (
              <p>
                <strong>Brand’s internal name:</strong> {sale.internalName}
              </p>
            )}
            <p>
              <strong>Source:</strong> {sale.source === 'shopify' ? 'Shopify' : 'Manual entry'}
            </p>
            {sale.creatorVisibleAt && (
              <p>
                <strong>Creator preview starts:</strong> {formatEasternDate(sale.creatorVisibleAt, true)} (ET)
              </p>
            )}
            {selectedPreviewAudience && (
              <p>
                <strong>Early preview audience:</strong> {previewAudienceDescription}
              </p>
            )}
            {sale.isCreatorPreviewEmbargoed && sale.displayState === 'upcoming' && (
              <p>
                <strong>Embargoed until the sale starts.</strong>
              </p>
            )}
            <div className='discount-rows'>
              {sale.promotionRows.map(row => (
                <div className='discount-row' key={row.id}>
                  <strong>
                    {row.shopifyCodeType === 'buy_x_get_y'
                      ? 'Buy X, get Y'
                      : row.rate != null
                      ? row.isPercentage
                        ? `${row.rate}% off`
                        : `${formatAmountInCurrency(row.rate, row.currency || 'USD')} off`
                      : row.title}
                  </strong>
                  {row.code && (
                    <span>
                      Code: <strong>{row.code}</strong>
                    </span>
                  )}
                  {row.discountMethod === 'automatic' && <span>Automatically applied</span>}
                  <span>
                    {formatEasternDate(row.claimStartsAt, true) || 'No start date'} — {formatEasternDate(row.claimEndsAt, true) || 'No end date'} (ET)
                  </span>
                  {row.minimumPurchaseAmount != null && (
                    <span>Minimum spend: {formatAmountInCurrency(row.minimumPurchaseAmount, row.currency || 'USD')}</span>
                  )}
                </div>
              ))}
            </div>
            {sale.disclaimerText && <p>{sale.disclaimerText}</p>}
            {sale.moderationUpdatedAt && (
              <p className='moderation-date'>Placement or visibility last changed {formatEasternDate(sale.moderationUpdatedAt, true)} (ET)</p>
            )}
          </td>
        </tr>
      )}
    </tbody>
  );
};
SalesAndPromotionsPanelResult.propTypes = {
  sale: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  updateSale: PropTypes.func.isRequired
};
export default SalesAndPromotionsPanelResult;
